import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Cloud, Boxes, GitBranch, LineChart, ShieldAlert, FileText, Settings2, Siren } from 'lucide-react';

const tools = [
  { name: 'Cloud console', icon: Cloud },
  { name: 'Kubernetes', icon: Boxes },
  { name: 'CI/CD', icon: GitBranch },
  { name: 'Monitoring', icon: LineChart },
  { name: 'Security', icon: ShieldAlert },
  { name: 'Logs', icon: FileText },
  { name: 'Infrastructure-as-Code', icon: Settings2 },
  { name: 'Incident response', icon: Siren },
];

const Problem: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="problem" className="py-32 relative overflow-hidden bg-dark-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-semibold mb-6">
            <span className="text-white">Infrastructure shouldn&apos;t require</span>
            <br />
            <span className="gradient-text">ten different consoles</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Modern engineering teams manage cloud resources, Kubernetes clusters, deployments,
            pipelines, logs, metrics, alerts, security findings, and infrastructure configuration —
            each in its own tool, with its own login and its own mental model.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-16"
        >
          {tools.map((tool) => (
            <div
              key={tool.name}
              className="glass rounded-xl px-4 py-5 flex flex-col items-center text-center space-y-2"
            >
              <tool.icon className="w-6 h-6 text-neon-blue" />
              <span className="text-sm text-gray-300">{tool.name}</span>
            </div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-lg text-gray-400 max-w-3xl mx-auto text-center"
        >
          DeploAI aims to bring these operational workflows into one intelligent control plane —
          not to replace the underlying cloud providers, Kubernetes, or CI/CD systems you already
          run, but to reduce how much time engineers spend moving between them.
        </motion.p>
      </div>
    </section>
  );
};

export default Problem;
